import ContainerWrapper from "@components/BasicComponents/Wrappers/ContainerWrapper/ContainerWrapper"
import {
	BlogPostModuleType,
	HeroModuleType,
} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import {clsx} from "clsx"
import {AnimatePresence, motion} from "framer-motion"
import React, {useRef, useState} from "react"

import HeroCurveBackground from "./HeroCurveBackground"
import HeroSlideContent from "./HeroSlideContent"
import styles from "./HomeHeroAnimated.module.scss"
import {useSvgHeight} from "./useSvgHeight"

interface HomeHeroAnimatedProps {
	data: HeroModuleType[]
	className?: string
	textColor?: "black" | "white"
	titleStyle?: string
	featuredPost?: BlogPostModuleType
}

const HomeHeroAnimated: React.FC<HomeHeroAnimatedProps> = ({
	data,
	className,
	textColor = "black",
	titleStyle = "",
	featuredPost,
}) => {
	const {ref} = useSvgHeight()
	const [currentIndex, setCurrentIndex] = useState(0)
	const isFirstRender = useRef(true)

	const slideOneFade =
		"linear-gradient(115deg, rgba(23, 94, 235, 1) 30%, rgba(23, 94, 235, 0.8) 53%, rgba(23, 51, 235, 0.3) 76%, rgba(23, 94, 235, 0.5) 100%)"

	const slideTwoFade =
		"linear-gradient(125deg, rgba(106, 152, 244, 1) 45%, rgba(121, 206, 255, 1) 65%, rgba(121, 166, 255, 0.6) 80%)"

	const slideThreeFade =
		// "linear-gradient(125deg, rgba(23, 94, 235, 0.1) 0%, rgba(23, 94, 235, 0.1) 45%, rgba(23, 94, 235, 0.3) 55%, rgba(190, 1, 189, 0.2) 65%, rgba(23, 94, 235, 0.3) 80%)"
		"linear-gradient(125deg, rgba(190, 1, 189, 1) 0%, rgba(190, 1, 189, 0.7) 40%, rgba(190, 1, 189, 0.6) 55%, rgba(190, 1, 189, 0.4) 65%, rgba(190, 1, 189, 0.5) 80%)"

	const colors = [slideOneFade, slideTwoFade, slideThreeFade]

	const currentColor = colors[currentIndex % colors.length]
	const hasWhiteBase = true

	return (
		<ContainerWrapper>
			<div style={{lineHeight: 0, position: "relative"}}>
				<div
					className={clsx(
						styles.mainBackground,
						className,
						data[currentIndex].backgroundVideoDesktop &&
							styles.backgroundTransparent,
					)}
				>
					<HeroSlideContent
						heroItem={data[currentIndex]}
						currentIndex={currentIndex}
						showCommonGraphic
						textColor={textColor}
						titleStyle={titleStyle}
						backgroundColor={currentColor}
						featuredPost={featuredPost}
					/>

					<div
						style={{
							position: "absolute",
							width: 0,
							height: 0,
							overflow: "hidden",
							opacity: 0,
							pointerEvents: "none",
						}}
						aria-hidden={"true"}
					>
						{data.map((item, index) => {
							if (index === currentIndex) return null
							const bgColor = colors[index % colors.length]
							return (
								<HeroSlideContent
									key={index}
									heroItem={item}
									currentIndex={index}
									textColor={textColor}
									titleStyle={titleStyle}
									backgroundColor={bgColor}
									featuredPost={featuredPost}
								/>
							)
						})}
					</div>

					<HeroCurveBackground
						backgroundVideoDesktop={
							data[currentIndex].backgroundVideoDesktop
						}
						refProp={ref}
						backgroundColor={currentColor}
						slideCount={data.length}
						activeIndex={currentIndex}
						autoDuration={5000}
						onChange={(index) => setCurrentIndex(index)}
					/>

					<AnimatePresence mode={"sync"}>
						<motion.div
							key={currentIndex}
							className={`${
								currentColor ? styles.backgroundTransparent : ""
							} ${styles.beforeBottomWhite}`}
							style={{
								position: "absolute",
								inset: 0,
								backgroundColor: hasWhiteBase
									? "#ffffff"
									: undefined,
								backgroundImage: currentColor,
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
							initial={{opacity: isFirstRender.current ? 1 : 0}}
							animate={{opacity: 1}}
							exit={{opacity: 0}}
							transition={{
								duration: isFirstRender.current ? 0 : 0.8,
								ease: "easeInOut",
							}}
							onAnimationComplete={() => {
								isFirstRender.current = false
							}}
						/>
					</AnimatePresence>
				</div>
			</div>
		</ContainerWrapper>
	)
}

export default HomeHeroAnimated
